import React, { useState, useMemo } from 'react'
import { z } from 'zod'
import useSWRMutation from 'swr/mutation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PlusIcon } from '@radix-ui/react-icons'
import Loader from '@/components/ui/Loader'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { postFetcher } from 'api/swr'
import { MyFromTextarea } from 'components/ui/MyFormField'
import { OcFields } from './fields.ts'
import { toast } from '@/components/ui/use-toast'

const CreatePage = ({ mutate }) => {
  const [open, setOpen] = useState(false)
  const defaultValues = useMemo(() => {
    const values = {}
    OcFields.forEach((section) => {
      section.items.forEach((item) => {
        values[item.key] = ''
      })
    })
    return values
  }, [])

  const { trigger, isMutating, error } = useSWRMutation('/ocs', postFetcher)

  const onSubmit = (data) => {
    if (isMutating) return
    const groupedData = {}
    OcFields.forEach((section) => {
      section.items.forEach((item) => {
        const value = data[item.key]
        if (value !== undefined && value !== null && value !== '') {
          groupedData[`oc_${section.key}_${item.key}`] = value
        }
      })
    })
    trigger(groupedData, {
      onSuccess: () => {
        form.reset(defaultValues)
        setOpen(false)
        mutate()
        toast({
          title: 'Success',
          description: 'Create successfully',
          className: 'bg-green-500 text-white',
        })
      },
      onError: (error) => {
        console.error('Create failed:', error)
        toast({
          variant: 'destructive',
          title: 'Failed',
          description: error.message || 'Create failed',
        })
      },
    })
  }

  const infoFormSchema = useMemo(() => {
    const shape = {}
    OcFields.forEach((section) => {
      section.items.forEach((item) => {
        const maxLength = item.maxLength ?? 100
        shape[item.key] = item.required
          ? z
              .string()
              .min(1, `${item.title} is required`)
              .max(maxLength, `${item.title} must be at most ${maxLength} characters`)
          : z
              .string()
              .max(maxLength, `${item.title} must be at most ${maxLength} characters`)
              .optional()
      })
    })
    return z.object(shape)
  }, [])

  const form = useForm({
    resolver: zodResolver(infoFormSchema),
    defaultValues,
  })
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2" />
          Create
        </Button>
      </DialogTrigger>
      <DialogContent className="dialog-content pr-0">
        <DialogHeader>
          <DialogTitle>Create</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 max-h-[80vh] overflow-y-auto pr-4"
          >
            {OcFields.map((section) => (
              <div key={section.key}>
                <h3 className="text-lg font-bold mb-2">{section.title}</h3>
                <div className="space-y-4">
                  {section.items.map((item) => (
                    <div key={item.key}>
                      <MyFromTextarea
                        name={item.key}
                        form={form}
                        label={item.title}
                        placeholder={item.placeholder}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {isMutating ? (
              <Loader>Updating...</Loader>
            ) : (
              <Button type="submit">Update</Button>
            )}
            {error && <div className="text-red-500">{error.message}</div>}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default CreatePage
